import React from 'react'
import { useParams } from 'react-router-dom'
import { Grid, GridItem } from '@which/seatbelt'

import type {
  Callout as CalloutType,
  ConsumerRegulations as ConsumerRegulationsType,
  ConsumerRightsVerticalQuery,
  LatestNews,
} from '../../generated/frontend'
import { useConsumerRightsVerticalQuery } from '../../generated/frontend'
import type { PageParams } from '../../routes'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import { AdviceCards } from '../../shared/components/AdviceCards/AdviceCards'
import { Breadcrumb } from '../../shared/components/Breadcrumb/Breadcrumb'
import { Callout } from '../../shared/components/Callout/Callout'
import { ConsumerRegulations } from '../../shared/components/ConsumerRegulations/ConsumerRegulations'
import { ErrorComponent } from '../../shared/components/Error/ErrorComponent'
import { PageError } from '../../shared/components/Error/PageError'
import { LatestNewsCards } from '../../shared/components/LatestNewsCards/LatestNewsCards'
import { Loader } from '../../shared/components/Loader/Loader'
import { LocalFooter } from '../../shared/components/LocalFooter/LocalFooter'
import { ResubscribeBannerWrapper } from '../../shared/components/ResubscribeBannerWrapper/ResubscribeBannerWrapper'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import type { PageInfo } from '../../shared/types/PageInfo'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { removeUrlQueryStringAndFragment } from '../../shared/utils/remove-url-query-string-and-fragment'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import { SubVerticalLinks } from './components/SubVerticalLinks/SubVerticalLinks'
import { VerticalHeader } from './components/VerticalHeader/VerticalHeader'
import styles from './template-styles/VerticalPage.module.scss'

const ConsumerRightsPage: React.FC = () => {
  const { verticalSlug } = useParams<PageParams>()
  const { loading, error, data } = useConsumerRightsVerticalQuery({
    variables: { verticalSlug },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })
  const { getBaseMetaTags } = useBaseMetaTags()

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.verticalPage) {
    return <PageError pageName="Consumer Rights Vertical Page" />
  }

  const {
    meta,
    header,
    callout,
    consumerRegulations,
    consumerRightsFooter,
    adviceCards,
    latestNews,
    subVerticalLinks,
    showResubBanner,
  } = data.verticalPage

  const baseMetaTags = getBaseMetaTags({ meta, imageUrl: header?.image?.src })
  const metaTags = getMetaTags({
    meta,
    baseMetaTags,
    verticalSlug,
    urls: { currentUrl: getFullUrl(), previousUrl },
    callout,
  })

  return (
    <div data-testid="template-portal" data-template-type="consumer-rights-vertical">
      <ResubscribeBannerWrapper showBanner={showResubBanner} />
      <Breadcrumb
        heading={{ text: header.title }}
        currentPage={{ text: header.title, href: '' }}
        links={[]}
      />
      <PageWrapperWithMeta metaTags={metaTags}>
        <Grid>
          <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
            <VerticalHeader {...header} size={1} className={styles.verticalHeader} />
          </GridItem>
          <GridItem span={{ large: 10 }} columnStart={{ large: 2 }}>
            <Callout
              title={callout?.title}
              byline={callout?.byline}
              description={callout?.description}
              items={callout?.items as CalloutType['items']}
              imageFormat={callout?.imageFormat ?? 'square'}
            />
          </GridItem>
          <GridItem span={{ medium: 10, large: 10 }} columnStart={{ medium: 2, large: 2 }}>
            <SubVerticalLinks {...subVerticalLinks} />
          </GridItem>
          {adviceCards?.cards?.length && (
            <GridItem>
              <AdviceCards className={styles.adviceCardsWrapper} {...adviceCards} />
            </GridItem>
          )}
          <GridItem span={{ small: 'full-bleed', medium: 'full-bleed', large: 'full-bleed' }}>
            <ConsumerRegulations data={consumerRegulations as ConsumerRegulationsType} />
          </GridItem>
          <GridItem>
            <GridItem span={{ small: 'full-bleed', medium: 'full-bleed', large: 'full-bleed' }}>
              <LatestNewsCards
                name="Consumer Rights"
                articles={latestNews.articles as LatestNews['articles']}
                viewAllData={{ text: 'View all news', href: '/news' }}
              />
            </GridItem>
            <LocalFooter data={consumerRightsFooter} />
          </GridItem>
        </Grid>
      </PageWrapperWithMeta>
    </div>
  )
}

export default ConsumerRightsPage

export type ConsumerRightsVerticalData = ConsumerRightsVerticalQuery['verticalPage'] & {
  __typename: 'VerticalPage'
}

const getMetaTags = ({
  meta,
  baseMetaTags,
  verticalSlug,
  urls,
  callout,
}: {
  meta: ConsumerRightsVerticalData['meta']
  baseMetaTags: JSX.Element[]
  verticalSlug: string
  urls: { currentUrl: string; previousUrl?: string }
  callout?: ConsumerRightsVerticalData['callout']
}) => {
  const pageInfo: PageInfo = {
    wcdPageUrl: removeUrlQueryStringAndFragment(urls.currentUrl),
    previousWcdPageUrl: urls.previousUrl || '',
    vertical: verticalSlug,
    content_type: 'Homepage',
    event: 'promoShown',
    promoContent: getPromoItemText(callout),
  }

  // data layer
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(pageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, pageInfo)

  return baseMetaTags.concat(dataLayer, dfpMeta)
}

const getPromoItemText = (callout?: ConsumerRightsVerticalData['callout']) => {
  const items = callout?.items ?? []
  const promoItemText = items.reduce((promoText: string, item, index) => {
    const separator = index + 1 < items.length ? ' | ' : ''
    return `${promoText}${item.title}${separator}`
  }, '')

  return promoItemText
}
